import { useCallback, useRef, useState } from 'react';
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';
import {
  steppedHPConfigurations,
  hpFormStepConfigurations,
  hangingProtocolStepIndex,
  hangingProtocolSubStepIndex,
  getStepsFromVersioned,
  applyVersionedStepsUpdate,
} from '../state';
import type { StepsUpdater } from '../state';
// Import via the Toaster barrel (not the concrete module) so unit tests that mock
// 'common/ui/Toaster' also intercept this hook's toaster dependency (VX-4444).
import { useToasterDispatch } from 'common/ui/Toaster';
import { createStepToastContentComponent, getDefaultHPToastOptions } from '../Steps/StepToast';
import { HP_TOAST_UNDO_DURATION } from '../Steps/hpStepToastMessages';
import { FORM_STEP_CREATED_TOAST, STEP_DELETED_TOAST } from '../Steps/stepsToastFactory';
import type { StepConfiguration } from '../steppedHPTypes';
import {
  BASE_STEP_NAME,
  createDefaultStep,
  generateDefaultStepName,
  isStepNameDuplicate,
} from '../hpStepController';
import { getStepSwitchInterceptor, getFormFlushInterceptor } from './useStepFormSync';
import { DropPosition } from '../Steps/dragDropUtils';
import { parseTempStepId } from '../tempStepId';
export const MAX_HP_STEPS = 30;

// Module-level set of active undo toast keys. Kept outside the hook so that
// dismissPendingUndoToasts works correctly even when StepBar temporarily
// unmounts and remounts (e.g., when the HP edit sidebar opens, ResizableStepBar
// returns null which unmounts StepBar; the keys must survive the remount).
const activeUndoToastKeys = new Set<string>();

/**
 * Resolves a step identifier to an array index. Handles both real smids and synthetic `__index_N`
 * fallback IDs generated by StepsConfigSection for steps without a backend smid.
 */
function resolveStepIndex(stepId: string, steps: StepConfiguration[]): number {
  const tempIndex = parseTempStepId(stepId);
  if (tempIndex !== null) {
    return tempIndex;
  }
  return steps.findIndex((step) => step.smid === stepId);
}

export type DropTarget = {
  index: number;
  position: DropPosition;
};

export type UseHPStepOperationsReturn = {
  steps: StepConfiguration[] | null;
  currentStepIndex: number;

  // Step lifecycle
  initializeSteps: (steps: StepConfiguration[] | null) => void;
  resetSteps: () => void;

  // Step CRUD
  addStep: () => void;
  deleteStep: (stepId: string, options?: { onDismiss?: () => void; onUndo?: () => void }) => void;

  renameStep: (stepId: string, newName: string) => void;
  setCurrentStepIndex: (index: number) => void;

  // Step navigation
  stepTotal: number;
  canGoBack: boolean;
  canGoForward: boolean;
  goToNextStep: () => void;
  goToPreviousStep: () => void;
  goToFirstStep: () => void;
  goToLastStep: () => void;

  // Drag-and-drop
  draggedIndex: number | null;
  dropTarget: DropTarget | null;
  handleStepBarDragStart: (index: number) => void;
  handleStepBarDragOver: (index: number, position: DropPosition) => void;
  handleStepBarDragEnd: () => void;
  /**
   * Applies a step-bar drag-drop reorder. Returns the resulting step smid order, or `null` when the
   * drop was a no-op (dropped back onto its own position, or nothing was being dragged), so callers
   * can move index-addressed side state (baseline entries, revert snapshots) along with the steps
   * without re-deriving the insert math.
   */
  handleStepBarDrop: (targetIndex: number) => ReadonlyArray<Nullable<string>> | null;

  // Rename UI state
  renamingStepId: string | null;
  handleStartRename: (stepId: string) => void;
  handleRenameConfirm: (stepId: string, newName: string) => void;
  handleRenameCancel: () => void;

  // Toast management
  dismissPendingUndoToasts: () => void;
};

/**
 * Injected atom accessors that decouple the core hook from which atoms it reads/writes.
 * Viewer mode and form mode provide different implementations.
 */
type StepAtomAccessors = {
  steps: StepConfiguration[] | null;
  setSteps: (updater: StepsUpdater) => void;
  /**
   * Syncs renames to the opposite atom (viewer ↔ form). Only `renameStep` calls
   * this; add/delete/reorder skip it intentionally. Null = no sync needed.
   */
  syncRename: ((updater: StepsUpdater) => void) | null;
  /** Flush interceptor for flushing form edits before add/delete. Null = none. */
  flushInterceptor: (() => StepConfiguration[] | null) | null;
};

/** Default step when a HP has been created */
export const DEFAULT_CREATE_HP_STEP: StepConfiguration = createDefaultStep({
  name: BASE_STEP_NAME,
  defaultLayout: '2,2',
});

/**
 * Core hook that implements step CRUD, navigation, drag-and-drop, rename, and undo.
 * Decoupled from specific atoms via injected accessors.
 */
const useStepOperationsCore = ({
  steps: stepConfigurations,
  setSteps: setStepConfigurations,
  syncRename,
  flushInterceptor,
}: StepAtomAccessors): UseHPStepOperationsReturn => {
  const [currentStepIndex, setCurrentStepIndex] = useRecoilState(hangingProtocolStepIndex);
  const stepTotal = stepConfigurations?.length ?? 0;
  const [, setSubStepIndex] = useRecoilState(hangingProtocolSubStepIndex);

  const { enqueueToast, closeToast } = useToasterDispatch();

  // Drag-and-drop state
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [dropTarget, setDropTarget] = useState<DropTarget | null>(null);

  // Rename state
  const [renamingStepId, setRenamingStepId] = useState<string | null>(null);

  // Undo snapshots for step deletion (one per pending delete toast)
  const undoDeleteSnapshotsRef = useRef<
    Map<string, { previousStepConfigurations: StepConfiguration[]; onUndo?: () => void }>
  >(new Map());

  // Track toast keys where undo was clicked, so the subsequent toast
  // onClose knows to skip the onDismiss callback (which flushes the
  // deferred backend deletion).
  const undoedDeleteToastKeysRef = useRef<Set<string>>(new Set());

  // Undo snapshot for step addition (stores pre-add state for undo)
  const undoAddSnapshotRef = useRef<{
    previousStepConfigurations: StepConfiguration[] | null;
    previousStepIndex: number;
    toastKey: string;
  } | null>(null);

  const initializeSteps = useCallback(
    (steps: StepConfiguration[] | null) => {
      setStepConfigurations(steps);
      setCurrentStepIndex(0);
      setSubStepIndex(0);
    },
    [setStepConfigurations, setCurrentStepIndex, setSubStepIndex]
  );

  const resetSteps = useCallback(() => {
    setStepConfigurations(null);
    setCurrentStepIndex(0);
    setSubStepIndex(0);
  }, [setStepConfigurations, setCurrentStepIndex, setSubStepIndex]);

  const handleUndoDelete = useCallback(
    (toastKey: string) => {
      const pending = undoDeleteSnapshotsRef.current.get(toastKey);
      if (!pending) return;

      setStepConfigurations(pending.previousStepConfigurations);
      // Let the caller (e.g. clinical mode) cancel its own pending backend
      // deletion and re-sync its dirty-state baseline for the restored step.
      pending.onUndo?.();

      // Mark this toast as undoed BEFORE closing so the onClose callback
      // skips onDismiss (which would otherwise flush the deferred deletion).
      undoedDeleteToastKeysRef.current.add(toastKey);
      undoDeleteSnapshotsRef.current.delete(toastKey);
      closeToast(toastKey);
    },
    [setStepConfigurations, closeToast]
  );

  const handleUndoAdd = useCallback(() => {
    const pending = undoAddSnapshotRef.current;
    if (!pending) return;

    setStepConfigurations(pending.previousStepConfigurations);
    setCurrentStepIndex(pending.previousStepIndex);

    closeToast(pending.toastKey);
    undoAddSnapshotRef.current = null;
  }, [setStepConfigurations, setCurrentStepIndex, closeToast]);

  const addStep = useCallback(() => {
    // When the form is open, flush current form edits into the step configurations
    // so the clone uses the latest viewport data (not stale closure values).
    const currentSteps = (flushInterceptor ? flushInterceptor() : null) ?? stepConfigurations ?? [];
    if (currentSteps.length >= MAX_HP_STEPS) return;

    // If there's an active undo toast from a previous add, dismiss it
    if (undoAddSnapshotRef.current) {
      closeToast(undoAddSnapshotRef.current.toastKey);
      undoAddSnapshotRef.current = null;
    }

    const toastKey = `step-added-${Date.now()}`;

    undoAddSnapshotRef.current = {
      previousStepConfigurations: currentSteps.length > 0 ? [...currentSteps] : null,
      previousStepIndex: currentStepIndex,
      toastKey,
    };

    const currentStep = currentSteps[currentStepIndex];
    const newStepName = generateDefaultStepName(currentSteps);

    const newStep = createDefaultStep({
      name: newStepName,
      sourceStep: currentStep,
    });

    const newTotal = currentSteps.length + 1;
    setStepConfigurations((prev) => [...(prev ?? []), newStep]);
    setCurrentStepIndex(newTotal - 1);

    activeUndoToastKeys.add(toastKey);
    enqueueToast(FORM_STEP_CREATED_TOAST.message, {
      ...getDefaultHPToastOptions({
        toastKey,
        autoHideDuration: HP_TOAST_UNDO_DURATION,
        onClose: () => {
          activeUndoToastKeys.delete(toastKey);
          if (undoAddSnapshotRef.current?.toastKey === toastKey) {
            undoAddSnapshotRef.current = null;
          }
          closeToast(toastKey);
        },
      }),
      ContentComponent: createStepToastContentComponent({
        actionLabel: FORM_STEP_CREATED_TOAST.actionLabel,
        countdownDuration: HP_TOAST_UNDO_DURATION,
        onAction: handleUndoAdd,
      }),
    });
  }, [
    flushInterceptor,
    stepConfigurations,
    currentStepIndex,
    setStepConfigurations,
    setCurrentStepIndex,
    closeToast,
    enqueueToast,
    handleUndoAdd,
  ]);

  const deleteStep = useCallback(
    (stepId: string, options?: { onDismiss?: () => void; onUndo?: () => void }) => {
      // When the form is open, flush current form edits so the undo
      // snapshot captures the latest viewport data.
      const currentSteps =
        (flushInterceptor ? flushInterceptor() : null) ?? stepConfigurations ?? [];
      if (currentSteps.length <= 1) return;

      // Validate the index before allocating the toast key / undo snapshot so a bad stepId
      // doesn't leave a stale entry in undoDeleteSnapshotsRef with no toast to clean it up.
      const deletedIndex = resolveStepIndex(stepId, currentSteps);
      if (deletedIndex < 0) return;

      const toastKey = `step-deleted-${Date.now()}`;

      undoDeleteSnapshotsRef.current.set(toastKey, {
        previousStepConfigurations: [...currentSteps],
        onUndo: options?.onUndo,
      });

      const newSteps = currentSteps.filter((_, i) => i !== deletedIndex);
      setStepConfigurations(newSteps);

      // Adjust current step index if needed
      if (currentStepIndex >= newSteps.length) {
        setCurrentStepIndex(newSteps.length - 1);
      } else if (deletedIndex < currentStepIndex) {
        setCurrentStepIndex(currentStepIndex - 1);
      }

      activeUndoToastKeys.add(toastKey);
      enqueueToast(STEP_DELETED_TOAST.message, {
        ...getDefaultHPToastOptions({
          toastKey,
          autoHideDuration: HP_TOAST_UNDO_DURATION,
          onClose: () => {
            activeUndoToastKeys.delete(toastKey);
            undoDeleteSnapshotsRef.current.delete(toastKey);
            // Skip onDismiss (which flushes the deferred deletion) when
            // undo was clicked — the undo handler marks the toast key.
            const wasUndoed = undoedDeleteToastKeysRef.current.has(toastKey);
            undoedDeleteToastKeysRef.current.delete(toastKey);
            if (!wasUndoed) {
              options?.onDismiss?.();
            }
            closeToast(toastKey);
          },
        }),
        ContentComponent: createStepToastContentComponent({
          actionLabel: STEP_DELETED_TOAST.actionLabel,
          countdownDuration: HP_TOAST_UNDO_DURATION,
          onAction: () => handleUndoDelete(toastKey),
          icon: STEP_DELETED_TOAST.icon,
        }),
      });
    },
    [
      flushInterceptor,
      stepConfigurations,
      currentStepIndex,
      setStepConfigurations,
      setCurrentStepIndex,
      closeToast,
      enqueueToast,
      handleUndoDelete,
    ]
  );

  const renameStep = useCallback(
    (stepId: string, newName: string) => {
      const trimmed = newName.trim();
      if (trimmed) {
        const currentSteps = stepConfigurations ?? [];
        if (isStepNameDuplicate(trimmed, currentSteps, stepId)) return;
        const targetIndex = resolveStepIndex(stepId, currentSteps);
        const updater = (prev: StepConfiguration[] | null) =>
          (prev ?? []).map((step, i) =>
            i === targetIndex ? { ...step, display_name: trimmed } : step
          );
        setStepConfigurations(updater);
        // Sync rename to the other atom if available
        if (syncRename) {
          syncRename(updater);
        }
      }
      setRenamingStepId(null);
    },
    [stepConfigurations, setStepConfigurations, syncRename]
  );

  const handleStepBarDragStart = useCallback((index: number) => {
    setDraggedIndex(index);
  }, []);

  const handleStepBarDragOver = useCallback(
    (index: number, position: DropPosition) => {
      if (draggedIndex !== null && draggedIndex !== index) {
        setDropTarget({ index, position });
      }
    },
    [draggedIndex]
  );

  const handleStepBarDragEnd = useCallback(() => {
    setDraggedIndex(null);
    setDropTarget(null);
  }, []);

  const handleStepBarDrop = useCallback(
    (targetIndex: number): ReadonlyArray<Nullable<string>> | null => {
      if (draggedIndex === null || dropTarget === null || stepConfigurations === null) {
        handleStepBarDragEnd();
        return null;
      }

      let insertIndex = targetIndex;
      if (dropTarget.position === DropPosition.After) {
        insertIndex = targetIndex + 1;
      }

      // Adjust if dragging from above/left to below/right
      if (draggedIndex < insertIndex) {
        insertIndex -= 1;
      }

      if (draggedIndex === insertIndex) {
        handleStepBarDragEnd();
        return null;
      }

      const newSteps = [...stepConfigurations];
      const [draggedStep] = newSteps.splice(draggedIndex, 1);
      newSteps.splice(insertIndex, 0, draggedStep);
      setStepConfigurations(newSteps);

      // Update active step index to follow the previously active step
      const currentActiveStep = stepConfigurations[currentStepIndex];
      if (currentActiveStep) {
        const newActiveIndex = newSteps.indexOf(currentActiveStep);
        if (newActiveIndex !== -1 && newActiveIndex !== currentStepIndex) {
          setCurrentStepIndex(newActiveIndex);
        }
      }

      handleStepBarDragEnd();

      return newSteps.map((step) => step.smid);
    },
    [
      draggedIndex,
      dropTarget,
      stepConfigurations,
      currentStepIndex,
      setStepConfigurations,
      setCurrentStepIndex,
      handleStepBarDragEnd,
    ]
  );

  const handleStartRename = useCallback((stepId: string) => {
    setRenamingStepId(stepId);
  }, []);

  const handleRenameConfirm = useCallback(
    (stepId: string, newName: string) => {
      renameStep(stepId, newName);
    },
    [renameStep]
  );

  const handleRenameCancel = useCallback(() => {
    setRenamingStepId(null);
  }, []);

  const effectiveStepCount = stepConfigurations?.length ?? 0;

  const canGoBack = currentStepIndex > 0;
  const canGoForward = currentStepIndex < effectiveStepCount - 1;

  const navigateToStep = useCallback(
    (target: number | 'first' | 'last' | 'next' | 'prev') => {
      let targetIndex: number;
      switch (target) {
        case 'first':
          targetIndex = 0;
          break;
        case 'last':
          targetIndex = Math.max(0, effectiveStepCount - 1);
          break;
        case 'next':
          targetIndex = currentStepIndex + 1 >= effectiveStepCount ? 0 : currentStepIndex + 1;
          break;
        case 'prev':
          targetIndex = Math.max(0, currentStepIndex - 1);
          break;
        default:
          targetIndex = target;
      }
      const interceptor = getStepSwitchInterceptor();
      if (interceptor) {
        interceptor(targetIndex);
      } else {
        setCurrentStepIndex(targetIndex);
      }
      setSubStepIndex(0);
    },
    [currentStepIndex, effectiveStepCount, setCurrentStepIndex, setSubStepIndex]
  );

  const goToPreviousStep = useCallback(() => navigateToStep('prev'), [navigateToStep]);
  const goToNextStep = useCallback(() => navigateToStep('next'), [navigateToStep]);
  const goToFirstStep = useCallback(() => navigateToStep('first'), [navigateToStep]);
  const goToLastStep = useCallback(() => navigateToStep('last'), [navigateToStep]);

  /** Dismiss all active undo toasts (add/delete) without triggering undo. */
  const dismissPendingUndoToasts = useCallback(() => {
    for (const key of activeUndoToastKeys) {
      closeToast(key);
    }
    activeUndoToastKeys.clear();
    undoDeleteSnapshotsRef.current.clear();
    undoedDeleteToastKeysRef.current.clear();
    undoAddSnapshotRef.current = null;
  }, [closeToast]);

  return {
    steps: stepConfigurations,
    currentStepIndex,
    initializeSteps,
    resetSteps,
    addStep,
    deleteStep,
    renameStep,
    setCurrentStepIndex,
    stepTotal,
    canGoBack,
    canGoForward,
    goToNextStep,
    goToPreviousStep,
    goToFirstStep,
    goToLastStep,
    draggedIndex,
    dropTarget,
    handleStepBarDragStart,
    handleStepBarDragOver,
    handleStepBarDragEnd,
    handleStepBarDrop,
    renamingStepId,
    handleStartRename,
    handleRenameConfirm,
    handleRenameCancel,
    dismissPendingUndoToasts,
  };
};

/**
 * Step operations for the viewer (clinical read mode).
 * Reads/writes the versioned steppedHPConfigurations atom.
 * Rename sync to the form atom is gated on formSteps != null.
 */
export const useViewerStepOperations = (): UseHPStepOperationsReturn => {
  const viewerVersioned = useRecoilValue(steppedHPConfigurations);
  const viewerSteps = getStepsFromVersioned(viewerVersioned);
  const setViewerVersioned = useSetRecoilState(steppedHPConfigurations);

  const formSteps = useRecoilValue(hpFormStepConfigurations);
  const setFormSteps = useSetRecoilState(hpFormStepConfigurations);

  const setSteps = useCallback(
    (updater: StepsUpdater) => {
      applyVersionedStepsUpdate(setViewerVersioned, updater);
    },
    [setViewerVersioned]
  );

  // Only sync renames to the form atom when the form has active data (E1 fix).
  // When formSteps is null (form closed), skip the write to avoid corrupting
  // the form atom from null → [].
  const setOtherSteps = useCallback(
    (updater: StepsUpdater) => {
      setFormSteps((prev) => {
        if (prev == null) return prev;
        return typeof updater === 'function' ? updater(prev) : updater;
      });
    },
    [setFormSteps]
  );

  return useStepOperationsCore({
    steps: viewerSteps,
    setSteps,
    syncRename: formSteps != null ? setOtherSteps : null,
    flushInterceptor: null,
  });
};

/**
 * Step operations for the HP edit form.
 * Reads/writes the plain hpFormStepConfigurations atom.
 * Flushes form edits before add/delete via getFormFlushInterceptor.
 */
export const useFormStepOperations = (): UseHPStepOperationsReturn => {
  const [formSteps, setFormSteps] = useRecoilState(hpFormStepConfigurations);
  const setViewerVersioned = useSetRecoilState(steppedHPConfigurations);

  const setSteps = useCallback(
    (updater: StepsUpdater) => {
      setFormSteps(updater);
    },
    [setFormSteps]
  );

  const setOtherSteps = useCallback(
    (updater: StepsUpdater) => {
      applyVersionedStepsUpdate(setViewerVersioned, updater);
    },
    [setViewerVersioned]
  );

  return useStepOperationsCore({
    steps: formSteps,
    setSteps,
    syncRename: setOtherSteps,
    flushInterceptor: getFormFlushInterceptor(),
  });
};

/**
 * @deprecated Use useViewerStepOperations() or useFormStepOperations() directly.
 * Kept for backward compatibility during migration.
 */
export const useHPStepOperations = (options?: {
  formMode?: boolean;
}): UseHPStepOperationsReturn => {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  return options?.formMode ? useFormStepOperations() : useViewerStepOperations();
};
